/* eslint-disable no-param-reassign */
import {
  ActionReducerMapBuilder,
  createAsyncThunk,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';

import DefaultApiLimit from '../../../config/defaultLimit';
import { filterType } from '../../../config/enum';
import {
  CommonFilterProperties,
  FilterUpdateProperties,
} from '../../../interface/filter.interface';
import {
  FacilityTags,
  FacilityTagsInitialProperties,
  FetchFacilityTagsProperties,
  FilterFacilityTagInterFace,
  RequestFacilityTagsInterFace,
} from '../../../interface/partner-interfaces/facility-tag.interface';
import {
  ClearFilters,
  ConfigureListFilterResponse,
  RemoveSingleFilterFromList,
  UpdateCommonFilter,
} from '../../../shared-functions/CommonFilters';
import { removeEmptyKeys } from '../../../shared-functions/DataTime';
import facilityTag from '../../../utils/api/partner/facility-tag';

export const fetchFacilityTagsList = createAsyncThunk(
  'facility-tags/fetchFacilityTagsList',
  async (_, { dispatch }) => {
    try {
      const response = await facilityTag.fetchFacilityTagsList();
      return response.data;
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationDetails',
        payload: {
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 200,
            message: error?.response?.data?.message || 'Something went wrong!',
          },
        },
      });
      throw error;
    }
  },
);

export const fetchCurrentFacilityTagsList = createAsyncThunk(
  'facility-tags/fetchCurrentFacilityTagsList',
  async (_, { dispatch }) => {
    try {
      const response = await facilityTag.fetchCurrentFacilityTagsList();
      response.data.results = response.data.results.concat({
        facilityTagName: 'allFacility',
        _id: 'all',
        isSelected: true,
      });

      return response.data;
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationDetails',
        payload: {
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 200,
            message: error?.response?.data?.message || 'Something went wrong!',
          },
        },
      });
      throw error;
    }
  },
);

export const fetchFacilityTags = createAsyncThunk(
  'facility-tags/fetchFacilityTags',
  async (data: RequestFacilityTagsInterFace, { dispatch }) => {
    try {
      const response = await facilityTag.fetchFacilityTags(removeEmptyKeys({ ...data }));
      return {
        ...response.data,
        results: response.data.results.map((facilityTags: FacilityTags) => ({
          ...facilityTags,
          id: facilityTags._id,
          chargingStations: facilityTags?.chargingStationsData, // As ChargingStationsData handled as chargingStation in all files
        })),
      };
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationDetails',
        payload: {
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 200,
            message: error?.response?.data?.message || 'Something went wrong!',
          },
        },
      });
      throw error;
    }
  },
);

export const fetchListFilters = createAsyncThunk<CommonFilterProperties[], void>(
  'facility-tags/fetchListFilters',
  async (_, { dispatch }) => {
    try {
      const response = await facilityTag.fetchListFilters();
      const updatedResponse: CommonFilterProperties[] = ConfigureListFilterResponse(response.data);
      return updatedResponse;
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationDetails',
        payload: {
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 200,
            message: error?.response?.data?.message || 'Something went wrong!',
          },
        },
      });
      throw error;
    }
  },
);

const stationFilter: CommonFilterProperties[] = [
  {
    name: 'searchPlaceholder',
    key: 'searchBy',
    type: filterType.TEXT,
    value: '',
    applyFilter: false,
    enable: true,
    searchBy: true,
  },
];

const initialState: FacilityTagsInitialProperties = {
  currentFacilityTagsList: [],
  facilityTagsList: [],
  facilityTags: [],
  loading: false,
  page: DefaultApiLimit.page,
  limit: DefaultApiLimit.limit,
  totalPages: 1,
  totalResults: 0,
  filter: {},
  filters: stationFilter,
  filterListLoading: false,
  isFilterUpdated: false,
  sortBy: null,
  sortField: null,
};

// Create the slice
export const facilityTagsSlice = createSlice({
  name: 'facility-tags',
  initialState,
  reducers: {
    /**
     * Set facility tag filter data function
     * @param {any} state - The current state.
     * @param {any} action - The action object.
     */
    setFacilityTagsFilterData: (
      state: FacilityTagsInitialProperties,
      action: PayloadAction<FilterFacilityTagInterFace>,
    ) => {
      state.filter = action.payload;
    },
    /**
     * change selected current facility tag data function
     * @param {any} state - The current state.
     * @param {any} action - The action object.
     */
    changeSelectedCurrentFacilityTag: (
      state: FacilityTagsInitialProperties,
      action: PayloadAction<FilterFacilityTagInterFace>,
    ) => {
      state.currentFacilityTagsList = state.currentFacilityTagsList.map((facilityTagTemporary) =>
        facilityTagTemporary.facilityTagName === action.payload.facilityTagName
          ? { ...facilityTagTemporary, isSelected: true }
          : { ...facilityTagTemporary, isSelected: false },
      );
    },

    /**
     * Update station management filters
     * @param {any} state - The current state.
     * @param {any} action - The action object.
     */
    updateFacilityFilters: (state, action: PayloadAction<FilterUpdateProperties>) => {
      const payload = { ...action.payload };
      if (payload.type === 'add') {
        const { updatedCommonFilter, isFilterUpdated } = UpdateCommonFilter(
          state.filters,
          action.payload,
        );
        state.filters = updatedCommonFilter;
        state.isFilterUpdated = isFilterUpdated;
        // state.filters = UpdateCommonFilter(state.filters, action.payload);
      } else if (payload.type === 'remove') {
        state.filters = RemoveSingleFilterFromList(state.filters, payload.key);
        state.isFilterUpdated = true;
      } else if (payload.type === 'clear') {
        state.filters = ClearFilters(state.filters);
        state.isFilterUpdated = true;
        state.sortBy = null;
        state.sortField = null;
      }
    },
    /**
     * function for set sortBy
     * @param {any} state - The current state.
     * @param {any} action - The action object.
     */
    setSortData: (state, action: PayloadAction<{ sortField: string; sortBy: 'asc' | 'desc' }>) => {
      state.sortField = action.payload.sortField;
      state.sortBy = action.payload.sortBy;
    },
  },
  /**
   * Extra reducers to handle thunk request
   * @param {any} builder - The builder object.
   */
  extraReducers: (builder: ActionReducerMapBuilder<FacilityTagsInitialProperties>) => {
    builder.addCase(fetchFacilityTagsList.fulfilled, (state, action) => {
      state.facilityTagsList = action.payload.results;
    });
    builder.addCase(fetchCurrentFacilityTagsList.fulfilled, (state, action) => {
      state.currentFacilityTagsList = action.payload.results;
    });
    builder
      .addCase(fetchFacilityTags.pending, (state) => {
        state.loading = true;
      })
      .addCase(
        fetchFacilityTags.fulfilled,
        (state, action: PayloadAction<FetchFacilityTagsProperties>) => {
          state.loading = false;
          state.page = action.payload.page;
          state.limit = action.payload.limit;
          state.totalPages = action.payload.totalPages;
          state.totalResults = action.payload.totalResults;
          state.facilityTags = action.payload.results;
        },
      )
      .addCase(fetchFacilityTags.rejected, (state) => {
        state.loading = false;
      });
    builder
      .addCase(fetchListFilters.pending, (state) => {
        state.filterListLoading = true;
      })
      .addCase(
        fetchListFilters.fulfilled,
        (state, action: PayloadAction<CommonFilterProperties[]>) => {
          state.filterListLoading = false;
          state.filters = [...(state.filters || []), ...action.payload];
        },
      )
      .addCase(fetchListFilters.rejected, (state) => {
        state.filterListLoading = false;
      });
  },
});

// Export the actions
export const {
  setFacilityTagsFilterData,
  changeSelectedCurrentFacilityTag,
  updateFacilityFilters,
  setSortData,
} = facilityTagsSlice.actions;

// Export the reducer
export default facilityTagsSlice.reducer;
